import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import './RadialMenu.css';

const W = 440, H = 440, CX = 220, CY = 220;
const RO = 192, RI = 80;
const ARC_TOTAL = Math.PI * (200 / 180);

const ICON_URLS = {
  dashboard: 'https://cdn-icons-png.flaticon.com/512/4725/4725672.png',
  logout: 'https://cdn-icons-png.flaticon.com/512/18280/18280100.png',
  organs: 'https://cdn-icons-png.flaticon.com/512/4785/4785728.png',
  codes: 'https://cdn-icons-png.flaticon.com/512/16136/16136332.png',
  code: 'https://cdn-icons-png.flaticon.com/512/16136/16136332.png',
  search: 'https://cdn-icons-png.flaticon.com/512/639/639426.png',
};

function getMenuFor(path) {
  const menus = {
    '/admin/dashboard': [
      { id: 'logout', label: 'Logout', icon: 'logout', onclick: (n, l, a) => { l(); a('/admin'); } },
    ],
    '/admin/organs': [
      { id: 'search', label: 'Search', icon: 'search', onclick: (n, l, a, s) => { if (s) s(); } },
      { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', onclick: (n, l, a) => a('/admin/dashboard') },
      { id: 'codes', label: 'Code Management', icon: 'code', onclick: (n, l, a) => a('/admin/codes') },
      { id: 'logout', label: 'Logout', icon: 'logout', onclick: (n, l, a) => { l(); a('/admin'); } },
    ],
    '/admin/codes': [
      { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', onclick: (n, l, a) => a('/admin/dashboard') },
      { id: 'organs', label: 'Organs Menu', icon: 'organs', onclick: (n, l, a) => a('/admin/organs') },
      { id: 'logout', label: 'Logout', icon: 'logout', onclick: (n, l, a) => { l(); a('/admin'); } },
    ],
  };
  return menus[path] || menus['/admin/dashboard'];
}

function RadialMenu({ onSearch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: authLogout } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [openAmt, setOpenAmt] = useState(0);
  const [pos, setPos] = useState(() => {
    const isMobile = window.innerWidth <= 1024; // Include iPads
    if (isMobile) {
      return { x: 30, y: window.innerHeight - 30 };
    }
    return { x: window.innerWidth - 76, y: window.innerHeight / 2 };
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const dragging = useRef(false);
  const dragSrc = useRef({ x: 0, y: 0 });
  const posSrc = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  const posRef = useRef(pos);
  posRef.current = pos;

  // Nudge the menu if it's too close to edges when opening
  // Snap to corner when closing
  useEffect(() => {
    const { x, y } = pos;
    const { innerWidth: w, innerHeight: h } = window;
    const snapMargin = 30;

    if (isOpen) {
      const margin = RO + 20;
      let newX = x;
      let newY = y;

      if (x < margin) newX = margin;
      else if (x > w - margin) newX = w - margin;

      if (y < margin) newY = margin;
      else if (y > h - margin) newY = h - margin;

      if (newX !== x || newY !== y) {
        setPos({ x: newX, y: newY });
      }
    } else if (!dragging.current) {
      const isDesktop = window.innerWidth > 1024;
      let targetX, targetY;

      if (isDesktop) {
        // 6-point snapping for desktop (Corners + Middles of vertical edges)
        const points = [
          { x: snapMargin, y: snapMargin },     // Top-Left
          { x: w - snapMargin, y: snapMargin }, // Top-Right
          { x: snapMargin, y: h / 2 },          // Left-Middle
          { x: w - snapMargin, y: h / 2 },      // Right-Middle
          { x: snapMargin, y: h - snapMargin }, // Bottom-Left
          { x: w - snapMargin, y: h - snapMargin } // Bottom-Right
        ];
        
        let minDist = Infinity;
        let closest = points[0];
        points.forEach(p => {
          const d = Math.sqrt((x - p.x)**2 + (y - p.y)**2);
          if (d < minDist) { minDist = d; closest = p; }
        });
        targetX = closest.x;
        targetY = closest.y;
      } else {
        // Standard 4-corner snapping for mobile/tablets
        targetX = x < w / 2 ? snapMargin : w - snapMargin;
        targetY = y < h / 2 ? snapMargin : h - snapMargin;
      }
      
      if (x !== targetX || y !== targetY) {
        setPos({ x: targetX, y: targetY });
      }
    }
  }, [isOpen]);

  const [hovered, setHovered] = useState(-1);
  const [centerLabel, setCenterLabel] = useState('Air command');
  
  const canvasRef = useRef(null);
  const openAmtRef = useRef(0);
  const imagesRef = useRef([]);
  const rafRef = useRef(null);

  const items = useMemo(() => getMenuFor(location.pathname), [location.pathname]);
  const N = items.length;
  const SLICE = ARC_TOTAL / N;

  const arcStart = useMemo(() => {
    const { x, y } = pos;
    const { innerWidth: w, innerHeight: h } = window;
    
    // Distances from edges
    const distL = x;
    const distR = w - x;
    const distT = y;
    const distB = h - y;

    const margin = RO + 20; // Radius + safety margin

    // Logic: Fan towards the area with the most space
    if (distL < margin && distT < margin) {
      // Top Left Corner -> Fan Down-Right
      return Math.PI * (20 / 180);
    } 
    if (distL < margin && distB < margin) {
      // Bottom Left Corner -> Fan Up-Right
      return Math.PI * (-70 / 180);
    }
    if (distR < margin && distT < margin) {
      // Top Right Corner -> Fan Down-Left
      return Math.PI * (110 / 180);
    }
    if (distR < margin && distB < margin) {
      // Bottom Right Corner -> Fan Up-Left
      return Math.PI * (200 / 180);
    }

    // Edge Cases
    if (distL < margin) return Math.PI * (-45 / 180); // Left Edge -> Fan Right
    if (distR < margin) return Math.PI * (135 / 180); // Right Edge -> Fan Left
    if (distT < margin) return Math.PI * (45 / 180);  // Top Edge -> Fan Down
    if (distB < margin) return Math.PI * (-135 / 180); // Bottom Edge -> Fan Up

    // Default (Center-ish) -> Fan Left
    return Math.PI * (170 / 180);
  }, [pos]);

  useEffect(() => {
    const imgs = items.map(item => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = ICON_URLS[item.icon] || ICON_URLS.logout;
      return img;
    });
    imagesRef.current = imgs;
  }, [items]);

  const getSliceA = useCallback((i, open) => {
    const s = arcStart + SLICE * i * open;
    const e = arcStart + SLICE * (i + 1) * open;
    return { s, e, m: (s + e) / 2 };
  }, [arcStart, SLICE]);

  const drawTeal = useCallback((cx, label) => {
    const grad = cx.createRadialGradient(CX - 10, CY - 10, 4, CX, CY, RI - 2);
    grad.addColorStop(0, '#4DD8E8');
    grad.addColorStop(0.5, '#00BCD4');
    grad.addColorStop(1, '#00838F');

    cx.beginPath();
    cx.arc(CX, CY, RI - 2, 0, Math.PI * 2);
    cx.fillStyle = grad;
    cx.fill();

    cx.beginPath();
    cx.arc(CX, CY, RI - 2, 0, Math.PI * 2);
    cx.strokeStyle = 'rgba(255,255,255,0.3)';
    cx.lineWidth = 2;
    cx.stroke();

    if (label) {
      const words = label.split(' ');
      cx.textAlign = 'center';
      cx.textBaseline = 'middle';
      cx.font = '600 13px "Segoe UI", sans-serif';
      cx.fillStyle = '#ffffff';
      if (words.length === 1) {
        cx.fillText(label, CX, CY);
      } else {
        cx.fillText(words[0], CX, CY - 9);
        cx.fillText(words.slice(1).join(' '), CX, CY + 9);
      }
    }
  }, []);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const cx = cv.getContext('2d');
    const open = openAmtRef.current;

    cx.clearRect(0, 0, W, H);

    for (let i = 0; i < N; i++) {
      const { s, e, m } = getSliceA(i, open);
      const isH = i === hovered;

      cx.beginPath();
      cx.moveTo(CX, CY);
      cx.arc(CX, CY, RO, s, e);
      cx.closePath();
      cx.fillStyle = isH ? '#ffffff' : '#f0f2f4';
      cx.fill();
      cx.strokeStyle = '#cdd5d9';
      cx.lineWidth = 1.2;
      cx.stroke();

      cx.beginPath();
      cx.moveTo(CX, CY);
      cx.arc(CX, CY, RI + 2, s, e);
      cx.closePath();
      cx.fillStyle = isH ? '#ffffff' : '#f0f2f4';
      cx.fill();

      if (open > 0.5) {
        const img = imagesRef.current[i];
        if (img && img.complete && img.naturalWidth > 0) {
          const ir = (RI + RO) / 2;
          const ix = CX + ir * Math.cos(m);
          const iy = CY + ir * Math.sin(m);
          const alpha = Math.max(0, (open - 0.5) / 0.5);
          const sz = 34;
          cx.save();
          cx.globalAlpha = alpha;
          cx.drawImage(img, ix - sz / 2, iy - sz / 2, sz, sz);
          cx.restore();
        }
      }
    }

    drawTeal(cx, centerLabel);
  }, [N, getSliceA, hovered, centerLabel, drawTeal]);

  useEffect(() => {
    const animate = () => {
      const target = isOpen ? 1 : 0;
      const d = target - openAmtRef.current;
      if (Math.abs(d) < 0.001) {
        openAmtRef.current = target;
        setOpenAmt(target);
        draw();
        rafRef.current = null;
        return;
      }
      openAmtRef.current += d * 0.13;
      setOpenAmt(openAmtRef.current);
      draw();
      rafRef.current = requestAnimationFrame(animate);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, draw]);

  useEffect(() => {
    draw();
  }, [hovered, centerLabel, draw]);

  const hitSlice = useCallback((mx, my) => {
    const dx = mx - CX, dy = my - CY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < RI || dist > RO) return -1;
    let a = Math.atan2(dy, dx);
    if (a < 0) a += Math.PI * 2;
    
    const open = openAmtRef.current;
    for (let i = 0; i < N; i++) {
      const { s, e } = getSliceA(i, open);
      let ss = ((s % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      let ee = ((e % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      if (ss > ee) {
        if (a >= ss || a <= ee) return i;
      } else {
        if (a >= ss && a <= ee) return i;
      }
    }
    return -1;
  }, [N, getSliceA]);

  const getXY = useCallback((e) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const s = e.touches ? e.touches[0] : e;
    return {
      x: (s.clientX - r.left) * (W / r.width),
      y: (s.clientY - r.top) * (H / r.height),
    };
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isOpen) return;
    const { x, y } = getXY(e);
    const idx = hitSlice(x, y);
    if (idx !== hovered) {
      setHovered(idx);
      setCenterLabel(idx >= 0 ? items[idx].label : 'Air command');
    }
  }, [isOpen, getXY, hitSlice, hovered, items]);

  const onMouseLeave = useCallback(() => {
    setHovered(-1);
    setCenterLabel('Air command');
  }, []);

  const onClick = useCallback((e) => {
    if (hasMoved.current) return;
    
    const { x, y } = getXY(e);
    const dx = x - CX, dy = y - CY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist <= RI) {
      setIsOpen(prev => !prev);
      return;
    }

    if (isOpen) {
      const idx = hitSlice(x, y);
      if (idx >= 0) {
        const item = items[idx];
        const doNav = (path) => navigate(path);
        const doLogout = () => authLogout();
        const doSearch = () => { if (onSearch) onSearch(); };
        item.onclick(doNav, doLogout, (p) => navigate(p), doSearch);
        setIsOpen(false);
      }
    }
  }, [getXY, isOpen, hitSlice, items, navigate, authLogout, onSearch]);

  const onDown = useCallback((e) => {
    dragging.current = true;
    setIsDragging(true);
    hasMoved.current = false;
    const p = e.touches ? e.touches[0] : e;
    dragSrc.current = { x: p.clientX, y: p.clientY };
    posSrc.current = { ...posRef.current };
  }, []);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - dragSrc.current.x;
    const dy = p.clientY - dragSrc.current.y;
    
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasMoved.current = true;
    }

    setPos({
      x: Math.max(30, Math.min(window.innerWidth - 30, posSrc.current.x + dx)),
      y: Math.max(30, Math.min(window.innerHeight - 30, posSrc.current.y + dy)),
    });
  }, []);

  const onUp = useCallback(() => {
    dragging.current = false;
    setIsDragging(false);
    // We delay resetting hasMoved to allow onClick to see it
    setTimeout(() => {
        if (!dragging.current) hasMoved.current = false;
    }, 50);
  }, []);

  useEffect(() => {
    const opt = { passive: false };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, opt);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [onMove, onUp]);

  return (
    <div className="rm-root">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="rm-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <div 
        className={`rm-wrap ${isDragging ? 'is-dragging' : ''}`} 
        style={{ left: pos.x, top: pos.y }}
      >
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="rm-canvas"
          style={{
            opacity: openAmt,
            pointerEvents: openAmt > 0.01 ? 'auto' : 'none',
          }}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          onMouseDown={onDown}
          onTouchStart={onDown}
        />

        <button
          className="rm-fab"
          onMouseDown={onDown}
          onTouchStart={onDown}
          onClick={(e) => {
            if (!hasMoved.current) {
                e.stopPropagation();
                setIsOpen(prev => !prev);
            }
          }}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          style={{ opacity: isOpen ? 0 : 1, pointerEvents: isOpen ? 'none' : 'auto', transition: 'opacity 0.2s ease' }}
        >
          <motion.svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="12" x2="20" y2="12" /><line x1="12" y1="4" x2="12" y2="20" />
          </motion.svg>
        </button>
      </div>
    </div>
  );
}

export default RadialMenu;
